/**
 * useExecutorFromChain — DAO-FOUND-03
 *
 * Fallback executor lookup for proposals where the chain says executed but
 * the backend has no executor row. Two real-world causes:
 *   1. Fixture data — the on-chain seeder (scripts/seed-dao-proposals.ts in
 *      arandao-contracts) calls executeTransaction but never POSTs
 *      /dao/:index/execute, so backend.executor stays null.
 *   2. Prod failure recovery — useExecuteProposal succeeded on chain but the
 *      backend POST failed (network blip, JWT expiry). The executor's own
 *      session has a retry button; everyone else viewing the proposal got
 *      stuck with no executor info until backend was reconciled.
 *
 * Reads the DAO contract's ExecuteTransaction(address indexed owner,
 * uint256 indexed txIndex) event and returns the latest matching owner.
 *
 * Block window: LOOKBACK_BLOCKS back from latest. ~33 min on Polygon's
 * ~2s block time. Two reasons it stays narrow:
 *   - Hardhat fork proxies eth_getLogs for pre-fork-start blocks to the
 *     upstream provider (drpc.org). Upstream rejects wide ranges with
 *     400 Bad Request, so the whole query fails. Keeping the window inside
 *     fork-local blocks avoids the proxy entirely.
 *   - Prod RPC providers cap eth_getLogs ranges anyway (typically 10k).
 *
 * Trade-off: only covers same-session post-execute backend-write failures.
 * Older proposals (or seeded ones where the seeder ran hours ago) won't
 * be found here — BottomCard's "این اقدام اجرا شده است." fallback handles
 * that case. To extend the window, fix the upstream provider's range cap
 * or query an indexed source (TheGraph/etc.) instead.
 *
 * Topic filter note: we do NOT pass `args: { txIndex }` to viem. Hardhat's
 * EDR rejects eth_getLogs requests that contain a `null` topic placeholder
 * (which viem emits for the un-filtered `owner` topic when `txIndex` is
 * provided but `owner` isn't), with "Missing or invalid parameters". We
 * fetch all ExecuteTransaction events in the window and filter client-side
 * by `txIndex`. Volume is tiny — DAO executions are rare even on prod —
 * so this stays cheap.
 *
 * Nickname resolution: if the chain-found wallet happens to be a registered
 * user, GET /user/by-addresses returns their nickname. Hardhat fixture
 * wallets (e.g. #14 for seeded P3) won't match — returns null nickname and
 * ExecutorRow falls back to wallet-only display.
 */

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useBlockNumber, usePublicClient } from "wagmi";
import { EXPECTED_CHAIN_ID } from "@/configs/expected-chain";
import { DAO_ABI } from "@/contract/abi/dao";
import { ContractAddress } from "@/contract/addresses";
import { $api } from "@/lib/api-client";

const LOOKBACK_BLOCKS = 1_000n;

export function useExecutorFromChain(txIndex: number, enabled: boolean) {
  const publicClient = usePublicClient({ chainId: EXPECTED_CHAIN_ID });
  const { data: latestBlock } = useBlockNumber({ chainId: EXPECTED_CHAIN_ID });

  const eventQuery = useQuery({
    queryKey: ["dao-executor-chain", txIndex, latestBlock?.toString() ?? null],
    enabled: enabled && !!publicClient && latestBlock !== undefined,
    retry: false,
    queryFn: async () => {
      if (!publicClient || latestBlock === undefined) return null;
      const fromBlock =
        latestBlock > LOOKBACK_BLOCKS ? latestBlock - LOOKBACK_BLOCKS : 0n;
      type ExecEvent = Awaited<
        ReturnType<typeof publicClient.getContractEvents<typeof DAO_ABI>>
      >[number];
      let events: ExecEvent[];
      try {
        events = await publicClient.getContractEvents({
          abi: DAO_ABI,
          address: ContractAddress.DAO,
          eventName: "ExecuteTransaction",
          fromBlock,
          toBlock: latestBlock,
        });
      } catch (err) {
        // Don't crash the page on RPC failures — log once and let the
        // BottomCard fallback render the executed-without-attribution text.
        console.warn(
          "[useExecutorFromChain] getContractEvents failed",
          {
            fromBlock: fromBlock.toString(),
            toBlock: latestBlock.toString(),
            txIndex,
          },
          err,
        );
        return null;
      }
      const target = BigInt(txIndex);
      const match = events.filter((e) => e.args.txIndex === target).at(-1);
      return match?.args.owner ?? null;
    },
  });

  const executorAddress = eventQuery.data ?? undefined;

  const nicknameQuery = $api.useQuery(
    "get",
    "/user/by-addresses",
    { params: { query: { a: executorAddress ? [executorAddress] : [] } } },
    { enabled: !!executorAddress },
  );

  const nickname = useMemo<string | null>(() => {
    if (!executorAddress) return null;
    const match = nicknameQuery.data?.data?.find(
      (u) => u.wallet_address.toLowerCase() === executorAddress.toLowerCase(),
    );
    return match?.nickname ?? null;
  }, [executorAddress, nicknameQuery.data]);

  return {
    executorAddress,
    nickname,
    isLoading:
      eventQuery.isLoading || (!!executorAddress && nicknameQuery.isLoading),
  };
}
